<template><div><code-well-header>
  <dt-dropdown navigation-type="arrow-keys">
    <template #anchor="{ attrs }">
      <dt-button v-bind="attrs">
        Click to Open
      </dt-button>
    </template>
    <template #list="{ close }">
      <dt-list-item-group
        heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
        heading="Menu Heading A"
      >
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 1
        </dt-list-item>
        <dt-dropdown-separator />
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 2
        </dt-list-item>
      </dt-list-item-group>
      <dt-dropdown-separator />
      <dt-list-item-group
        heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
        heading="Menu Heading B"
      >
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 3
        </dt-list-item>
      </dt-list-item-group>
    </template>
  </dt-dropdown>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the Dropdown component when you have a list of links or actions that can be performed. Use the <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select Menu</RouteLink> component to allow the users to choose one option from the list.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The dropdown component provides accessibility controls when using with List item component.</li>
<li class="d-docsite--list-element">The dropdown has two major pieces that are provided as required named slots: the anchor and the list.</li>
<li class="d-docsite--list-element">The <strong>anchor</strong> slot contains the element that controls the dropdown's visibility.</li>
<li class="d-docsite--list-element">The <strong>list</strong> slot contains a list of items to show as dropdown menu items.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-dropdown navigation-type="arrow-keys">
    <template #anchor="{ attrs }">
      <dt-button v-bind="attrs">
        Click to open
      </dt-button>
    </template>
    <template #list="{ close }">
      <dt-list-item
        v-for="(item) in items"
        :key="item.id"
        role="menuitem"
        :navigation-type="arrow-keys"
        @click="close"
      >
        {{ item.name }}
      </dt-list-item>
    </template>
  </dt-dropdown>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <div class="d-popover">
    <div id="DtPopover__anchor2">
      <button class="base-button__button d-btn d-btn--primary">
        <span class="d-btn__label base-button__label"> Click to open </span>
      </button>
    </div>
  </div>
</div>
<div class="tippy-box d-ps-absolute" data-tippy-root="" id="tippy-13" data-popper-placement="bottom" style="...">
  <div id="dt7" role="menu" aria-hidden="false" aria-labelledby="DtPopover__anchor8" aria-modal="false" class="d-popover__dialog d-popover__dialog--modal" tabindex="-1" style="...">
    <div class="d-popover__content">
      <ul id="dt6" class="d-dropdown-list d-py0">
        <li class="d-list-item" tabindex="-1" role="menuitem" aria-selected="false">
          <div class="d-item-layout">
            <section class="d-item-layout--content">
              <div class="d-item-layout--title">Menu Item 1</div>
            </section>
          </div>
        </li>
        ...
      </ul>
    </div>
  </div>
</div>
'
vueCode='
<dt-dropdown navigation-type="arrow-keys">
  <template #anchor="{ attrs }">
    <dt-button v-bind="attrs">
      Click to open
    </dt-button>
  </template>
  <template #list="{ close }">
    <dt-list-item
      v-for="(item) in items"
      :key="item.id"
      role="menuitem"
      :navigation-type="arrow-keys"
      @click="close"
    >
      {{ item.name }}
    </dt-list-item>
  </template>
</dt-dropdown>
'
/>
<h3 id="with-sections-and-headings" tabindex="-1" class="d-docsite--header-3">With Sections and Headings <a class="header-anchor d-link d-docsite--link d-link" href="#with-sections-and-headings" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-dropdown navigation-type="arrow-keys">
    <template #anchor="{ attrs }">
      <dt-button v-bind="attrs">
        Click to open
      </dt-button>
    </template>
    <template #list="{ close }">
      <dt-list-item-group
        heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
        heading="Menu Heading A"
      >
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 1
        </dt-list-item>
        <dt-dropdown-separator />
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 2
        </dt-list-item>
      </dt-list-item-group>
      <dt-dropdown-separator />
      <dt-list-item-group
        heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
        heading="Menu Heading B"
      >
        <dt-list-item
          role="menuitem"
          navigation-type="arrow-keys"
          @click="close"
        >
          Menu Item 3
        </dt-list-item>
      </dt-list-item-group>
    </template>
  </dt-dropdown>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <div class="d-popover">
    <div id="DtPopover__anchor2">
      <button class="base-button__button d-btn d-btn--primary">
        <span class="d-btn__label base-button__label"> Click to open </span>
      </button>
    </div>
  </div>
</div>
<div class="tippy-box d-ps-absolute" data-tippy-root="" id="tippy-13" data-popper-placement="bottom" style="...">
  <div id="dt7" role="menu" aria-hidden="false" aria-labelledby="DtPopover__anchor8" aria-modal="false" class="d-popover__dialog d-popover__dialog--modal" tabindex="-1" style="...">
    <div class="d-popover__content">
      <ul id="dt6" class="d-dropdown-list d-py0">
        <ul id="dt16" class="d-list-item-group" role="group" aria-labelledby="dt16-heading">
          <li id="dt16-heading" role="presentation" class="dt-dropdown-list--header d-py4 d-px8 d-fw-semibold d-c-default">Menu Heading A</li>
          <li id="dt17" class="d-list-item d-list-item--static" tabindex="-1" role="listitem">
            <div class="d-item-layout">
              <section class="d-item-layout--content">
                <div class="d-item-layout--title">
                  Menu Item 1
                </div>
              </section>
            </div>
          </li>
          <li aria-hidden="true" class="d-list-item-separator"></li>
          <li id="dt18" class="d-list-item d-list-item--static" tabindex="-1" role="listitem">
            <div class="d-item-layout">
              <section class="d-item-layout--content">
                <div class="d-item-layout--title">
                  Menu Item 2
                </div>
              </section>
            </div>
          </li>
        </ul>
        <li aria-hidden="true" class="d-list-item-separator"></li>
        <ul id="dt19" class="d-list-item-group" role="group" aria-labelledby="dt19-heading">
          <li id="dt19-heading" role="presentation" class="dt-dropdown-list--header d-py4 d-px8 d-fw-semibold d-c-default">Menu Heading B</li>
          <li id="dt20" class="d-list-item d-list-item--static" tabindex="-1" role="listitem">
            <div class="d-item-layout">
              <section class="d-item-layout--content">
                <div class="d-item-layout--title">
                  Menu Item 3
                </div>
              </section>
            </div>
          </li>
        </ul>
      </ul>
    </div>
  </div>
</div>
'
vueCode='
<dt-dropdown navigation-type="arrow-keys">
  <template #anchor="{ attrs }">
    <dt-button v-bind="attrs">
      Click to open
    </dt-button>
  </template>
  <template #list="{ close }">
    <dt-list-item-group
      heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
      heading="Menu Heading A"
    >
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 1
      </dt-list-item>
      <dt-dropdown-separator />
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 2
      </dt-list-item>
    </dt-list-item-group>
    <dt-dropdown-separator />
    <dt-list-item-group
      heading-class="d-py4 d-px8 d-fw-semibold d-c-default"
      heading="Menu Heading B"
    >
      <dt-list-item
        role="menuitem"
        navigation-type="arrow-keys"
        @click="close"
      >
        Menu Item 3
      </dt-list-item>
    </dt-list-item-group>
  </template>
</dt-dropdown>
'
showHtmlWarning />
<h3 id="context-menu" tabindex="-1" class="d-docsite--header-3">Context Menu <a class="header-anchor d-link d-docsite--link d-link" href="#context-menu" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Set <code v-pre>openOnContext=true</code> to open the menu on right-click (context menu) and disable the default trigger behavior.</p>
<code-well-header>
  <dt-dropdown navigation-type="arrow-keys" :open-on-context="true">
    <template #anchor="{ attrs }">
      <div
        v-bind="attrs"
        class="d-ba d-bas-dashed d-w264 d-py48 d-ta-center d-bgc-black-300"
      >
        Right click to open
      </div>
    </template>
    <template #list="{ close }">
      <dt-list-item
        v-for="(item) in items"
        :key="item.id"
        role="menuitem"
        :navigation-type="arrow-keys"
        @click="close"
      >
        {{ item.name }}
      </dt-list-item>
    </template>
  </dt-dropdown>
</code-well-header>
<code-example-tabs
vueCode='
<dt-dropdown navigation-type="arrow-keys" :open-on-context="true">
  <template #anchor="{ attrs }">
    <div
      v-bind="attrs"
      class="d-ba d-bas-dashed d-w264 d-py48 d-ta-center d-bgc-black-300"
    >
      Right click to open
    </div>
  </template>
  <template #list="{ close }">
    <dt-list-item
      v-for="(item) in items"
      :key="item.id"
      role="menuitem"
      :navigation-type="arrow-keys"
      @click="close"
    >
      {{ item.name }}
    </dt-list-item>
  </template>
</dt-dropdown>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="dropdown" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
<p class="d-docsite--paragraph">The dropdown menu has a role of &quot;menu&quot; and the list items have a role &quot;menuitem&quot;. See <a href="https://www.w3.org/WAI/ARIA/apg/#menubutton" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">W3C guidelines</a> for more information.</p>
<h3 id="list" tabindex="-1" class="d-docsite--header-3">List <a class="header-anchor d-link d-docsite--link d-link" href="#list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The Dropdown is rendered with semantic <code v-pre>&lt;ul&gt;</code> and <code v-pre>&lt;li&gt;</code>. The list slot is wrapped in <code v-pre>&lt;ul&gt;</code> element so make sure the items are wrapped in <code v-pre>&lt;li&gt;</code> element and each item has the menuitem role. It is recommended to use the <RouteLink to="/components/list-item.html" class="d-docsite--link d-link">List Item component</RouteLink> as it supports all the necessary accessibility props and interactions.</p>
<h3 id="focus-keyboard" tabindex="-1" class="d-docsite--header-3">Focus &amp; Keyboard <a class="header-anchor d-link d-docsite--link d-link" href="#focus-keyboard" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A dropdown menu has well defined standard <a href="https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-links/#kbd_label" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">keyboard interactions</a> that it should support. The focus will be set to the the active item on keyboard navigation. <code v-pre>UP</code> and <code v-pre>DOWN</code> arrows move the highlighted selection between the list items. <code v-pre>HOME</code> and <code v-pre>END</code> keys will jump straight to the first or the last item. Once the beginning or the end of the list is reached the selection will move to the opposite end of the list by default. If you want custom behavior you can pass <code v-pre>onBeginningOfList</code> and <code v-pre>onEndOfList</code> methods as props. <code v-pre>A-Z</code> keys will cycle through items that start with the corresponding letter.</p>
<p class="d-docsite--paragraph">Pressing <code v-pre>ESC</code> key while the dropdown has focus will emit an <strong>escape</strong> event and will close the menu. When the <strong>highlight</strong> selection changes, a highlight event is emitted with the index of the currently highlighted item. <code v-pre>SPACE</code> key will open the dropdown if it's closed and select the item when it's pressed on a list item.</p>
</div></template>


<script setup>
const items = [
  { name: 'Menu item 1', id: 1 },
  { name: 'Menu item 2', id: 2 },
  { name: 'Another menu item 1', id: 3 },
  { name: 'Menu item 3', id: 4 },
  { name: 'Another menu item 2', id: 5 },
];
</script>